import { Box, createStyles, Group, Skeleton, Stack } from '@mantine/core';
import type {
	DescriptionCreationProposal,
	SecodaAIProposalOut,
} from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { useSecodaEntity } from '../../../../api';
import { SecodaEntityIcon } from '../../../SecodaEntity';

const useStyles = createStyles((theme) => ({
	column: {
		flexGrow: 1,
		flexShrink: 1,
		flex: 1,
	},
}));

export function DescriptionSuggestionRenderer({
	proposal,
	data,
}: {
	proposal: SecodaAIProposalOut;
	data: DescriptionCreationProposal;
}) {
	const { classes } = useStyles();
	const { data: entity } = useSecodaEntity({
		id: data.data.entity_id,
	});

	// Derive old description directly from proposal metadata or entity description
	const oldDescription =
		proposal.metadata.old_description ?? entity?.description;

	if (!entity) {
		return (
			<Stack spacing="md" p="xl">
				<Group spacing="sm" align="center">
					<Skeleton circle height={32} width={32} />
					<Skeleton height={24} width={200} />
				</Group>
				<Skeleton height={24} width={200} />
				<Skeleton height={64} />
				<Skeleton height={24} width={200} />
				<Skeleton height={64} />
			</Stack>
		);
	}

	return (
		<Box p="xl">
			<Group spacing="sm" align="center" mb="md">
				<SecodaEntityIcon entity={entity} size={24} />
				<Text size="xl" weight="bold">
					{entity.title}
				</Text>
			</Group>

			<Group spacing="lg" align="flex-start" noWrap>
				<Box className={classes.column}>
					<Text
						size="sm"
						weight="semibold"
						color="text/secondary/default"
						mb="xs"
					>
						Current
					</Text>
					<Text size="md">
						{oldDescription && oldDescription !== ''
							? oldDescription
							: 'No description available'}
					</Text>
				</Box>

				<Box className={classes.column}>
					<Text
						size="sm"
						weight="semibold"
						color="text/secondary/default"
						mb="xs"
					>
						Suggested
					</Text>
					<Text size="md" color="text/ai/default">
						{data.data.description}
					</Text>
				</Box>
			</Group>
		</Box>
	);
}
